import { Link } from 'react-router-dom';
import { useApi } from '../lib/use-api';
import { money, relativeTime } from '../lib/api';
import { ErrorBanner, Loading, Panel, Stat, Status, Empty } from '../components/ui';

interface Summary {
  revenue: { gross: number; tax: number; paidConnections: number; earningDevices: number };
  fleet: { total: number; active: number; idle: number; deployed: number; online: number };
  receivables: { outstanding: number; overdue: number; collected: number };
}

interface Ticket {
  id: string;
  reference: string;
  subject: string;
  raised_by: string;
  status: string;
  device_reference: string | null;
  opened_at: string;
}

interface Rollout {
  id: string;
  name: string;
  version: string;
  status: string;
}

/** Wireframe C1 — the landing screen after staff sign-in. */
export function Dashboard() {
  const summary = useApi<Summary>('/internal/financials/summary');
  const tickets = useApi<{ items: Ticket[] }>('/internal/tickets?status=open');
  const rollouts = useApi<{ items: Rollout[] }>('/internal/firmware/rollouts');
  const offline = useApi<{ items: Array<{ deviceId: string; lastCheckinAt: string | null; status: string }> }>(
    '/internal/devices?status=active&limit=200',
  );

  if (summary.loading) return <Loading />;

  const fleet = summary.data?.fleet;
  // "Offline" is a device we believe is deployed but that has not reported in.
  // Deriving it here rather than asking the API keeps the definition next to
  // the number the staff member is looking at.
  const staleDevices = (offline.data?.items ?? []).filter(
    (device) => !device.lastCheckinAt || Date.now() - new Date(device.lastCheckinAt).getTime() > 180_000,
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Fleet dashboard</h1>
          <p>Everything deployed, earning and waiting on us.</p>
        </div>
      </div>

      <ErrorBanner error={summary.error} />

      <div className="cards">
        <Stat label="Devices active" value={fleet?.active ?? 0} sub={`${fleet?.deployed ?? 0} deployed in total`} />
        <Stat
          label="Devices offline"
          value={staleDevices.length}
          sub={staleDevices.length > 0 ? 'No check-in in 3 minutes' : 'All active devices reporting'}
        />
        <Stat
          label="Monetisation collected"
          value={money(summary.data?.revenue.gross)}
          sub={`${summary.data?.revenue.paidConnections ?? 0} paid connections`}
        />
        <Stat
          label="Open tickets"
          value={tickets.data?.items.length ?? 0}
          sub={`${money(summary.data?.receivables.outstanding)} receivable`}
        />
      </div>

      <Panel
        title="Firmware rollout progress"
        action={<Link to="/firmware">Manage firmware</Link>}
      >
        {rollouts.data?.items.length ? (
          <table>
            <thead>
              <tr>
                <th>Rollout</th>
                <th>Version</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rollouts.data.items.slice(0, 5).map((rollout) => (
                <tr key={rollout.id}>
                  <td>{rollout.name}</td>
                  <td className="mono">{rollout.version}</td>
                  <td>
                    <Status value={rollout.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No firmware rollouts yet.</Empty>
        )}
      </Panel>

      <Panel title="Recent tickets" action={<Link to="/tickets">All tickets</Link>}>
        {tickets.data?.items.length ? (
          <table>
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Raised by</th>
                <th>Device</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Opened</th>
              </tr>
            </thead>
            <tbody>
              {tickets.data.items.slice(0, 8).map((ticket) => (
                <tr key={ticket.id}>
                  <td className="mono">{ticket.reference}</td>
                  <td>{ticket.raised_by.replace(/_/g, ' ')}</td>
                  <td className="mono">
                    {ticket.device_reference ? (
                      <Link to={`/devices/${ticket.device_reference}`}>{ticket.device_reference}</Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{ticket.subject}</td>
                  <td>
                    <Status value={ticket.status} />
                  </td>
                  <td className="muted">{relativeTime(ticket.opened_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No open tickets.</Empty>
        )}
      </Panel>
    </>
  );
}
